/**
 * Grilla de instancias Oviyam: binning + índice actual.
 * Series grandes (100–N) → como máximo MAX_VISIBLE_SQUARES cuadraditos;
 * cada uno representa un rango de instancias/frames.
 */

export const MAX_VISIBLE_SQUARES = 64

/**
 * @param {number} total
 * @returns {number}
 */
export function resolveSquareCount(total) {
  const t = Math.max(0, Math.floor(Number(total) || 0))
  if (t <= 0) return 0
  return Math.min(t, MAX_VISIBLE_SQUARES)
}

/**
 * Rango de instancias [start, end) cubierto por el cuadrado `squareIndex`.
 * @param {number} squareIndex
 * @param {number} squareCount
 * @param {number} total
 * @returns {{ start: number, end: number }}
 */
export function squareInstanceRange(squareIndex, squareCount, total) {
  const sc = Math.max(1, Math.floor(Number(squareCount) || 1))
  const t = Math.max(0, Math.floor(Number(total) || 0))
  const i = Math.max(0, Math.min(sc - 1, Math.floor(Number(squareIndex) || 0)))
  if (t <= 0) return { start: 0, end: 0 }
  const start = Math.floor((i * t) / sc)
  const end = Math.floor(((i + 1) * t) / sc)
  return { start, end: Math.max(start + 1, end) }
}

/**
 * Cuadrado que contiene la instancia/frame `instanceIndex`.
 * @param {number} instanceIndex
 * @param {number} squareCount
 * @param {number} total
 * @returns {number} índice de cuadrado o -1
 */
export function instanceToSquareIndex(instanceIndex, squareCount, total) {
  const sc = Math.floor(Number(squareCount) || 0)
  const t = Math.floor(Number(total) || 0)
  if (!(sc > 0) || !(t > 0)) return -1
  const idx = Math.max(0, Math.min(t - 1, Math.floor(Number(instanceIndex) || 0)))
  // Evitar sesgo en el último bin con floor(idx * sc / t)
  const sq = Math.min(sc - 1, Math.floor((idx * sc) / t))
  return sq
}

/**
 * ¿El rango [start,end) está listo?
 * @param {number} start
 * @param {number} end
 * @param {(instanceIndex: number) => boolean} isReadyAt
 * @returns {boolean} true si al menos la mitad del bin está lista (o 1 si bin unitario)
 */
export function isBinReady(start, end, isReadyAt) {
  const a = Math.max(0, Math.floor(Number(start) || 0))
  const b = Math.max(a, Math.floor(Number(end) || 0))
  const len = b - a
  if (len <= 0) return false
  if (typeof isReadyAt !== 'function') return false
  if (len === 1) return !!isReadyAt(a)
  let ready = 0
  for (let i = a; i < b; i++) {
    if (isReadyAt(i)) ready += 1
  }
  return ready * 2 >= len
}

/**
 * Construye el modelo de cuadrados para la grilla.
 * @param {{
 *   total: number,
 *   currentIndex?: number|null,
 *   isReadyAt: (instanceIndex: number) => boolean,
 *   isActiveSeries?: boolean,
 * }} opts
 * @returns {Array<{ ready: boolean, current: boolean, start: number, end: number }>}
 */
export function buildInstanceSquares(opts) {
  const total = Math.max(0, Math.floor(Number(opts && opts.total) || 0))
  const squareCount = resolveSquareCount(total)
  if (!squareCount) return []
  const isReadyAt = opts && opts.isReadyAt
  const isActive = !!(opts && opts.isActiveSeries)
  const cur =
    opts && opts.currentIndex != null && Number.isFinite(Number(opts.currentIndex))
      ? Math.max(0, Math.floor(Number(opts.currentIndex)))
      : -1
  const currentSq =
    isActive && cur >= 0 ? instanceToSquareIndex(cur, squareCount, total) : -1

  const out = []
  for (let i = 0; i < squareCount; i++) {
    const { start, end } = squareInstanceRange(i, squareCount, total)
    out.push({
      ready: isBinReady(start, end, isReadyAt),
      current: i === currentSq,
      start,
      end,
    })
  }
  return out
}

/**
 * ¿Instancia/frame lista para pintar cuadrado?
 * Multi-SOP: file[i] ready. Monofile MF: framesReadyCount / Set / SOP completo.
 * @param {object[]} files
 * @param {number} instanceIndex
 * @param {(f: object) => boolean} isFileReady
 * @param {(f: object) => number} resolveFrames
 * @returns {boolean}
 */
export function isSeriesInstanceReady(files, instanceIndex, isFileReady, resolveFrames) {
  if (!Array.isArray(files) || !files.length) return false
  const idx = Math.max(0, Math.floor(Number(instanceIndex) || 0))
  const nf0 =
    typeof resolveFrames === 'function' ? resolveFrames(files[0]) : 0

  // Monofile multiframe: un SOP, N frames
  if (files.length === 1 && nf0 > 1) {
    const f = files[0]
    if (typeof isFileReady === 'function' && !isFileReady(f)) return false
    if (f && f.framesReadySet && typeof f.framesReadySet.has === 'function') {
      return f.framesReadySet.has(idx)
    }
    const n = Number(f && f.framesReadyCount)
    if (Number.isFinite(n) && n >= 0) return idx < n
    // SOP listo sin tracker → frames disponibles (decode on-demand)
    return true
  }

  const f = files[idx]
  return typeof isFileReady === 'function' ? !!isFileReady(f) : !!(f && f.imageId)
}
